import { Form, Modal, Input, DatePicker, TimePicker, message, Button, Row, Col, Select, Table, Typography } from "antd";
import { useEffect, useState } from "react";
import AnimateItems from "../../../utils/AnimateItems";
import axios from "axios";
import { useAuth } from "../../../layout/auth";
import { listasInspeccionFijas, listasInspeccionVariables } from "./formOptions";
import moment from "moment";
import "moment/locale/es";
moment.locale("es-mx");

export default function DesarrolloForm(props) {
  const { title, mainData: visita, setMainData, visible, setVisible } = props;
  const [form] = Form.useForm();
  const [selectedOption, setSelectedOption] = useState("Fija");
  const [visitHistory, setVisitHistory] = useState([]);
  const [dateSelection, setDateSelection] = useState(null);
  const [previewVisit, setPreviewVisit] = useState(null);

  const auth = useAuth();

  const handleSelectLista = (value) => {
    form.setFieldsValue({
      ListaInspeccion: value === "Fija" ? null : ["GENERALES"],
    });
    setSelectedOption(value);
  };

  useEffect(() => {
    if (visible) {
      form.resetFields();
      setSelectedOption(visita?.ListaTipo);
      setPreviewVisit(null);
      setVisitHistory([]);

      const parsedLista = visita?.ListaInspeccion
        ? visita.ListaInspeccion.split(",")
            .map((i) => i.trim())
            .filter((i) => i !== "")
        : [];

      form.setFieldsValue({
        ...visita,
        FechaVisita: moment(visita?.FechaVisita, "YYYY-MM-DD").isValid() ? moment(visita?.FechaVisita, "YYYY-MM-DD") : null,
        HoraInicio: moment(visita?.HoraInicio, "HH:mm:ss").isValid() ? moment(visita?.HoraInicio, "HH:mm:ss") : null,
        HoraTermino: moment(visita?.HoraTermino, "HH:mm:ss").isValid() ? moment(visita?.HoraTermino, "HH:mm:ss") : null,
        ListaInspeccion: visita?.ListaTipo === "Variable" ? parsedLista : parsedLista[0] || null,
      });
      if (visita?.FechaVisita) {
        setDateSelection(moment(visita.FechaVisita, "YYYY-MM-DD").format("DD/MM/YYYY"));
      }
    }
  }, [visita, form, visible]);

  useEffect(() => {
    if (dateSelection || previewVisit) {
      const d1 = moment(dateSelection, "DD/MM/YYYY").subtract(1, "days").format("YYYY-MM-DD");
      const d2 = moment(dateSelection, "DD/MM/YYYY").add(1, "days").format("YYYY-MM-DD");

      axios
        .get(`${process.env.REACT_APP_API_URL}/visitas?u=${auth.UID}&d1=${d1}&d2=${d2}`)
        .then((res) => {
          // Filter out current visit from history if we are editing
          const mergedHistory = res.data.filter((v) => v.VisitaID !== visita.VisitaID);

          if (previewVisit) {
            mergedHistory.push(previewVisit);
          }

          // Determine collisions
          const current = previewVisit || {
            FechaVisita: moment(visita.FechaVisita, "YYYY-MM-DD").format("YYYY-MM-DD"),
            HoraInicio: visita.HoraInicio,
            HoraTermino: visita.HoraTermino,
          };

          const historyWithCollisions = mergedHistory.map((v) => {
            if (v.isCurrent) return v;

            const visitDate = moment(v.FechaVisita, "YYYY-MM-DD").format("YYYY-MM-DD");

            // Simple overlap check: (StartA < EndB) && (EndA > StartB)
            // Only check collision if we have both times for the current edit
            const isOverlap =
              current.HoraTermino &&
              current.FechaVisita === visitDate &&
              current.HoraInicio < v.HoraTermino &&
              current.HoraTermino > v.HoraInicio;

            return { ...v, isCollision: isOverlap };
          });

          const sortedHistory = historyWithCollisions.sort((a, b) => {
            const timeA = moment(`${a.FechaVisita} ${a.HoraInicio}`, "YYYY-MM-DD HH:mm:ss").valueOf();
            const timeB = moment(`${b.FechaVisita} ${b.HoraInicio}`, "YYYY-MM-DD HH:mm:ss").valueOf();
            return timeA - timeB;
          });

          setVisitHistory(sortedHistory);
        })
        .catch((err) => {
          console.error("Error fetching visits:", err);
        });
    }
  }, [dateSelection, previewVisit, auth.UID, visita.VisitaID, visita.FechaVisita, visita.HoraInicio, visita.HoraTermino]);

  const handleFormChange = (_, allValues) => {
    const { FechaVisita, HoraInicio, HoraTermino } = allValues;

    if (FechaVisita && HoraInicio) {
      setPreviewVisit({
        ControlInterno: "Visita actual (cambios)",
        FechaVisita: moment(FechaVisita).format("YYYY-MM-DD"),
        HoraInicio: moment(HoraInicio, "HH:mm").format("HH:mm:ss"),
        HoraTermino: HoraTermino ? moment(HoraTermino, "HH:mm").format("HH:mm:ss") : "",
        isCurrent: true,
      });
    }
  };

  const columns = [
    {
      title: "Control Interno",
      dataIndex: "ControlInterno",
      key: "ControlInterno",
    },
    {
      title: "Fecha",
      dataIndex: "FechaVisita",
      key: "FechaVisita",
      render: (text) => moment(text, "YYYY-MM-DD").format("DD/MM/YYYY"),
    },
    {
      title: "Inicio",
      dataIndex: "HoraInicio",
      key: "HoraInicio",
      render: (text) => moment(text, "HH:mm:ss").format("HH:mm"),
    },
    {
      title: "Término",
      dataIndex: "HoraTermino",
      key: "HoraTermino",
      render: (text) => (text ? moment(text, "HH:mm:ss").format("HH:mm") : "-"),
    },
    {
      title: "Colonia",
      dataIndex: "ColoniaOAsentamiento",
      key: "ColoniaOAsentamiento",
    },
  ];

  return (
    <Modal title={title} visible={visible} onCancel={() => setVisible(false)} footer={null} width={800} centered>
      <Form form={form} layout="vertical" onValuesChange={handleFormChange}>
        <AnimateItems delay={0.15}>
          <Row gutter={[16, 16]}>
            <Col span={8}>
              <Form.Item name="FechaVisita" label="Fecha de visita" rules={[{ required: true, message: "Campo requerido" }]}>
                <DatePicker
                  format={"DD/MM/YYYY"}
                  style={{ width: "100%" }}
                  disabledDate={(current) => current && current > moment().endOf("day")}
                  onChange={(date, dateString) => setDateSelection(dateString)}
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item name="HoraInicio" label="H. Inicio" rules={[{ required: true, message: "Campo requerido" }]}>
                <TimePicker
                  format={"HH:mm"}
                  style={{ width: "100%" }}
                  disabledTime={() => ({ disabledHours: () => [0, 1, 2, 3, 4, 5, 6, 21, 22, 23] })}
                  minuteStep={5}
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item name="HoraTermino" label="H. Fin" rules={[{ required: true, message: "Campo requerido" }]}>
                <TimePicker
                  format={"HH:mm"}
                  style={{ width: "100%" }}
                  disabledTime={() => ({ disabledHours: () => [0, 1, 2, 3, 4, 5, 6, 21, 22, 23] })}
                  minuteStep={5}
                />
              </Form.Item>
            </Col>
          </Row>

          {visitHistory.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <Typography.Text strong>Otras visitas del día:</Typography.Text>
              <Table
                dataSource={visitHistory}
                columns={columns}
                size="small"
                pagination={false}
                rowClassName={(record) => {
                  if (record.isCurrent) return "fake-visit";
                  if (record.isCollision) return "colliding-visit";
                  return "";
                }}
              />
            </div>
          )}

          <Form.Item name="ListaTipo" label="Lista tipo">
            <Select style={{ width: "100%" }} placeholder="Selecciona una lista tipo" onChange={handleSelectLista}>
              <Select.Option value="Fija">Fija</Select.Option>
              <Select.Option value="Variable">Variable</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="ListaInspeccion" label="Lista de inspección">
            {selectedOption === "Variable" ? (
              <Select mode="multiple" style={{ width: "100%" }} placeholder="Selecciona una lista de inspección">
                {listasInspeccionVariables.map((lista) => (
                  <Select.Option key={lista.value} value={lista.label}>
                    {lista.label}
                  </Select.Option>
                ))}
              </Select>
            ) : (
              <Select style={{ width: "100%" }} placeholder="Selecciona una lista de inspección">
                {listasInspeccionFijas.map((lista) => (
                  <Select.Option key={lista.value} value={lista.label}>
                    {lista.label}
                  </Select.Option>
                ))}
              </Select>
            )}
          </Form.Item>
          <Form.Item name="ObjetoVisita" label="Objeto de visita" rules={[{ required: true, message: "Campo requerido" }]}>
            <Input.TextArea rows={1} />
          </Form.Item>
          <Form.Item name="CircunstanciaVisita" label="Circunstancia de visita" rules={[{ required: true, message: "Campo requerido" }]}>
            <Input.TextArea rows={4} />
          </Form.Item>
          <Form.Item
            name="NoConformidadesEncargado"
            label="No conformidades del encargado"
            rules={[{ required: true, message: "Campo requerido" }]}
          >
            <Input.TextArea rows={1} />
          </Form.Item>
          <Form.Item
            name="ObservacionesEncargado"
            label="Observaciones del encargado"
            rules={[{ required: true, message: "Campo requerido" }]}
          >
            <Input.TextArea rows={1} />
          </Form.Item>
          <Form.Item name="Acciones" label="Acciones" rules={[{ required: true, message: "Campo requerido" }]}>
            <Input.TextArea rows={1} />
          </Form.Item>
        </AnimateItems>
      </Form>
      <Button
        type="primary"
        onClick={() => {
          form.validateFields().then((values) => {
            const visitData = {
              ...visita,
              ...values,
              FechaVisita: values.FechaVisita.format("YYYY-MM-DD"),
              HoraInicio: values.HoraInicio.format("HH:mm:ss"),
              HoraTermino: values.HoraTermino.format("HH:mm:ss"),
              ListaInspeccion: values.ListaInspeccion ? values.ListaInspeccion.toString() : "",
            };
            axios
              .put(`${process.env.REACT_APP_API_URL}/visitas/${visita.VisitaID}`, visitData)
              .then(() => {
                message.success("Campos actualizados correctamente");
                setVisible(false);
                axios
                  .get(`${process.env.REACT_APP_API_URL}/visitas/${visita.VerificacionID}`)
                  .then((res) => {
                    message.success("Lista de visitas actualizada correctamente");
                    setMainData(res.data);
                  })
                  .catch((err) => {
                    console.log(err);
                    message.error("Error al actualizar la lista de visitas");
                  });
              })
              .catch((err) => {
                console.log(err);
                const errorMsg = err.response?.data?.message || "Error al actualizar los campos";
                message.error(errorMsg);
              });
          });
        }}
      >
        Guardar
      </Button>
    </Modal>
  );
}